import React, {useEffect, useMemo, useRef, useState} from "react";
import {AdvancedMarker, APIProvider, InfoWindow, Map as GoogleMaps, Pin, Polyline, useAdvancedMarkerRef, useMap} from "@vis.gl/react-google-maps";
import {useQuery} from "@tanstack/react-query";
import {GoogleMapsOverlay} from "@deck.gl/google-maps";
import {HeatmapLayer} from "@deck.gl/aggregation-layers";
import CorporationService from "../../../../../../services/web/corporation-service.js";
import ProjectUtils from "../../../../../../utils/project-utils.js";
import {useSpiderfy} from "../../../../../../hooks/use-spiderfier-adv-marker.js";
import MapElements from "./map-elements.jsx";
import {FaTriangleExclamation, FaUpRightFromSquare, FaXmark} from "react-icons/fa6";
import Button from "../../../../../../components/button/button.jsx";
import {navigateHook} from "../../../../../../hooks/use-navigate.js";

const mapIds = ProjectUtils.googleMapsIds();
const DEFAULT_CENTER = {lat: -23.0, lng: -46.0};

const formatDateTime = (value) => {
    const date = ProjectUtils.toDate(value);
    return date ? ProjectUtils.formatDate(date) : "Não informado";
};

const normalizeLocations = (data) => {
    return (data?.apply?.locations ?? [])
        .flatMap(item => item.details ?? [])
        .map((item, index) => {
            const latitude = Number(item.latitude);
            const longitude = Number(item.longitude);

            if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;

            return {
                id: index,
                lat: latitude,
                lng: longitude,
                latitude,
                longitude,
                agent: item.info_window?.agent,
                agentEnd: item.info_window?.agent_end,
                citizen: item.info_window?.citizen,
                programs: item.info_window?.programs,
                eventAck: item.info_window?.event_ack,
                eventStart: item.info_window?.event_start,
                eventEnd: item.info_window?.event_end,
                sos_id: item.info_window?.sos_id
            };
        })
        .filter(Boolean);
};

const SosInfoLine = ({label, agent, date}) => (
    <div>
        {label} <span className="font-bold">{agent || "Não informado"}</span>

        { date && (
            <> em <span className="font-bold">{formatDateTime(date)}</span></>
        )}
    </div>
);

const SosInfoWindow = ({point, onClose}) => {
    const navigate = navigateHook();

    return (
        <div className="relative min-w-[320px] text-[15px] text-[#222]">

            <section className="pr-8 print:pr-0!">
                <div className="text-[22px] leading-tight">{point.citizen || "Não informado"}</div>

                { point.programs && (
                    <div>{point.programs}</div>
                )}

                { point.eventStart && (
                    <div>Acionado em <span className="font-bold">{formatDateTime(point.eventStart)}</span></div>
                )}
            </section>

            <FaXmark size={20} onClick={onClose} className={'absolute right-0 top-0 cursor-pointer print:hidden!'} title={'Fechar'}/>

            <hr className={"opacity-25"}/>

            <section className="flex flex-col gap-1">
                { point.agent ? (
                    <SosInfoLine label="Assumido por" agent={point.agent} date={point.eventAck}/>
                ) : (
                    <div className="flex flex-row gap-1 items-center text-[#ffa500]">
                        <FaTriangleExclamation/> <span>SOS não assumido</span>
                    </div>
                )}

                { point.eventEnd && (
                    <SosInfoLine label="Finalizado por" agent={point.agentEnd} date={point.eventEnd}/>
                )}
            </section>

            { point.sos_id && (
                <Button target={"primary"} className={'mt-4! h-[40px]! px-10! print:hidden!'} onClick={() => navigate(`/sos/event/view/${point.sos_id}`, true)}>
                    <FaUpRightFromSquare size={14}/> Abrir SOS
                </Button>
            )}

        </div>
    );
};

const SosMarker = ({point, isOpen, onClick, onClose}) => {
    const [markerRef, marker] = useAdvancedMarkerRef();

    return (
        <>
            <AdvancedMarker ref={markerRef} position={{lat: point.lat, lng: point.lng}} onClick={() => onClick(point)}>
                <Pin background="#ef2929" borderColor="#333333" glyphColor="#000"/>
            </AdvancedMarker>

            { isOpen && marker && (
                <InfoWindow anchor={marker} minWidth={360} maxWidth={550} maxHeight={1200} headerDisabled={true}>
                    <SosInfoWindow point={point} onClose={onClose}/>
                </InfoWindow>
            )}
        </>
    );
};

const SosMapLayer = ({points, mapMode, setMapMode, setMapLoaded}) => {
    const map = useMap();
    const overlayRef = useRef(null);
    const [activeId, setActiveId] = useState(null);
    const {markers, toggleSpiderfy, setSpiderState} = useSpiderfy(points, map);

    useEffect(() => {
        if (!map) return;

        const overlay = new GoogleMapsOverlay({});
        overlay.setMap(map);
        overlayRef.current = overlay;

        return () => {
            overlay.setProps({layers: []});
            overlay.setMap(null);

            if (overlayRef.current === overlay) overlayRef.current = null;
        };
    }, [map]);

    useEffect(() => {
        if (!map || !points.length || !window.google) return;

        const bounds = new window.google.maps.LatLngBounds();
        points.forEach(point => bounds.extend({lat: point.lat, lng: point.lng}));

        if (!bounds.isEmpty()) map.fitBounds(bounds, 40);
    }, [map, points]);

    useEffect(() => {
        if (!map || !overlayRef.current) return;

        if (mapMode !== "heatmap") {
            overlayRef.current.setProps({layers: []});
            return;
        }

        overlayRef.current.setProps({
            layers: [
                new HeatmapLayer({
                    id: "sos-events-heatmap",
                    data: points,
                    getPosition: point => [point.lng, point.lat],
                    getWeight: () => 1,
                    radiusPixels: 40,
                    intensity: 1,
                    threshold: 0.05
                })
            ]
        });
        overlayRef.current.setMap(map);
    }, [map, mapMode, points]);

    useEffect(() => {
        if (!map || !setMapLoaded) return;

        const listener = map.addListener("tilesloaded", () => setMapLoaded(true));

        return () => {
            listener.remove();
            setMapLoaded(false);
        };
    }, [map, setMapLoaded]);

    useEffect(() => {
        if (!map) return;

        const listener = map.addListener("click", () => {
            setActiveId(null);
            setSpiderState(null);
        });

        return () => listener.remove();
    }, [map, setSpiderState]);

    const handleMarkerClick = (point) => {
        const result = toggleSpiderfy(point);

        if (result?.type === "spiderfy") {
            setActiveId(null);
            return;
        }

        setActiveId(point.id);
    };

    return (
        <>
            <MapElements
                mapMode={mapMode}
                onChangeMode={setMapMode}
            />

            { mapMode === "marker" && markers.map(point => (
                <SosMarker
                    key={`sos-event-marker-${point.id}`}
                    point={point}
                    isOpen={activeId === point.id}
                    onClick={handleMarkerClick}
                    onClose={() => setActiveId(null)}
                />
            ))}

            { mapMode === "marker" && markers.map(point => {
                if (!point.original) return null;

                return (
                    <Polyline
                        key={`sos-event-marker-line-${point.id}`}
                        path={[point.original, {lat: point.lat, lng: point.lng}]}
                        strokeColor="#333"
                        strokeOpacity={0.65}
                        strokeWeight={2}
                    />
                );
            })}
        </>
    );
};

const MapContent = ({data, showMap = true, mapImage, mapMode, setMapMode, setMapLoaded}) => {
    const points = useMemo(() => normalizeLocations(data), [data]);

    const {data: corporation = null} = useQuery({
        queryKey: ["corporation"],
        queryFn: () => CorporationService.entity(),
    });

    const center = useMemo(() => {
        if (points.length) return {lat: points[0].lat, lng: points[0].lng};

        const lat = Number(corporation?.latitude);
        const lng = Number(corporation?.longitude);

        if (!Number.isNaN(lat) && !Number.isNaN(lng)) return {lat, lng};
        return DEFAULT_CENTER;
    }, [points, corporation]);

    if (!points.length) {
        return (
            <div className="chart-report">
                <span className="chart-title">Distribuição geográfica dos eventos</span>
                <div className="alert alert-info p-2 h6 text-center w-full">Nenhum registro encontrado no período informado.</div>
            </div>
        );
    }

    return (
        <div className="chart-report">
            <span className={"chart-title"}>Distribuição geográfica dos eventos</span>

            { mapMode === "heatmap" && mapImage && (
                <div className="hidden print:block print:break-before-avoid!">
                    <img className="h-[420px] w-full" src={mapImage} alt="Mapa de calor dos eventos SOS"/>
                </div>
            )}

            { showMap && corporation && (
                <div id="map-report-component-sos-management" className={`mt-3 h-[420px] w-full overflow-hidden border border-[#d8d8d8] print:break-before-avoid! ${mapMode === "heatmap" ? "print:hidden" : ""}`}>
                    <APIProvider apiKey={corporation?.maps_api_key ?? ""} language="pt-BR" libraries={["places"]}>
                        <GoogleMaps
                            id="map-report-component-sos-management-map"
                            ref={(mapInstance) => {
                                if (!mapInstance?.map) return;
                                mapInstance.map.getDiv().__google_map_instance = mapInstance.map;
                            }}
                            mapId={mapIds.default}
                            defaultZoom={13}
                            defaultCenter={center}
                            gestureHandling="greedy"
                            streetViewControl={false}
                            fullscreenControl={false}
                            mapTypeControl={false}
                            style={{height: "100%", width: "100%"}}
                        >

                            <SosMapLayer
                                points={points}
                                mapMode={mapMode}
                                setMapMode={setMapMode}
                                setMapLoaded={setMapLoaded}
                            />
                        </GoogleMaps>
                    </APIProvider>
                </div>
            )}
        </div>
    );
};

export default MapContent;